import React, { useState } from 'react'
import {
    Box,
    CircularProgress,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import FolderOpenIcon from '@material-ui/icons/FolderOpen'
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks'
import BusinessIcon from '@material-ui/icons/Business'
import AddIcon from '@material-ui/icons/Add'
import TouchAppOutlinedIcon from '@material-ui/icons/TouchAppOutlined'

import { ConfigOptionCard } from './ConfigOptionCard'
import { NameConfigDialog } from './NameConfigDialog'
import { SetupOriginLine } from './SetupOriginLine'
import {
    LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
    SetupArtifactKind,
    SetupConfigOption,
    SelectedSetupConfig,
    adoptIntoPropertyCatalog,
    createSelectedFromOption,
    formatModifiedLabel,
    formatTemplateOptionSummary,
    getEzosSetupTemplates,
    getOrgSetupTemplates,
    scratchDefaultName,
    touchExistingOption,
} from './setupArtifacts'
import {
    SECTION_TONES,
    SetupChooserLane,
    resolveSetupOrigin,
} from './setupChooserTones'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        height: '100%',
        minHeight: 0,
        boxSizing: 'border-box',
        padding: theme.spacing(0.5, 1, 1),
        display: 'flex',
        justifyContent: 'center',
        overflow: 'hidden',
    },
    panel: {
        width: '100%',
        maxWidth: LEASING_SEASON_CONFIG_CONTENT_MAX_WIDTH,
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid #D5DBE3',
        borderRadius: 12,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
        boxSizing: 'border-box',
    },
    panelHeader: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'flex-start',
        gap: theme.spacing(1.25),
        padding: theme.spacing(1.25, 1.5),
        borderBottom: '1px solid #E8ECF1',
        backgroundColor: theme.palette.grey[50],
    },
    headerIcon: {
        flexShrink: 0,
        width: 28,
        height: 28,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#ECEFF1',
        color: '#455A64',
    },
    headerTitle: {
        fontSize: '0.8125rem',
        fontWeight: 700,
        lineHeight: 1.35,
    },
    headerDescription: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.45,
        marginTop: 2,
    },
    loading: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
    },
    grid: {
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gridTemplateRows: 'repeat(2, minmax(0, 1fr))',
        gap: theme.spacing(1),
        padding: theme.spacing(1),
        [theme.breakpoints.down('xs')]: {
            gridTemplateColumns: '1fr',
            gridTemplateRows: 'repeat(4, minmax(140px, 1fr))',
            overflow: 'auto',
        },
    },
    section: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
        height: '100%',
        border: '1px solid #D5DBE3',
        borderRadius: 10,
        backgroundColor: theme.palette.background.paper,
        overflow: 'hidden',
        borderLeftWidth: 4,
    },
    sectionHeader: {
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(1, 1.25),
        borderBottom: '1px solid #E8ECF1',
        backgroundColor: theme.palette.grey[50],
    },
    sectionIcon: {
        width: 28,
        height: 28,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    sectionTitle: {
        fontWeight: 700,
        fontSize: '0.75rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.palette.text.primary,
    },
    sectionCount: {
        marginLeft: 'auto',
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.text.secondary,
        backgroundColor: theme.palette.common.white,
        border: '1px solid #D5DBE3',
        borderRadius: 999,
        padding: theme.spacing(0.25, 1),
    },
    sectionBody: {
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        padding: theme.spacing(1.25),
    },
    cardGrid: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: theme.spacing(1.25),
    },
    emptySection: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        padding: theme.spacing(1.5, 1),
        border: '1px dashed #C8CED6',
        borderRadius: 10,
        backgroundColor: theme.palette.grey[50],
        textAlign: 'center',
    },
}))

type PendingCreate =
    | { kind: 'ezos'; option: SetupConfigOption }
    | { kind: 'org'; option: SetupConfigOption }
    | { kind: 'scratch'; defaultName: string }

export const SetupArtifactChooser: React.FC<{
    kind: SetupArtifactKind
    /** Property-owned configs for Edit existing (lifted so adopts persist). */
    existing: SetupConfigOption[]
    onExistingChange: (next: SetupConfigOption[]) => void
    onSelected: (config: SelectedSetupConfig) => void
    discourageScratch?: boolean
    loading?: boolean
}> = ({
    kind,
    existing,
    onExistingChange,
    onSelected,
    discourageScratch = false,
    loading = false,
}) => {
    const classes = useStyles()
    const [pending, setPending] = useState<PendingCreate | null>(null)

    const ezos = getEzosSetupTemplates(kind)
    const org = getOrgSetupTemplates(kind)

    const closeDialog = () => setPending(null)

    const handleConfirmName = (name: string) => {
        if (!pending) return
        const trimmed = name.trim() || scratchDefaultName(kind)

        if (pending.kind === 'scratch') {
            const { selected, nextExisting } = adoptIntoPropertyCatalog(
                existing,
                {
                    name: trimmed,
                    source: 'scratch',
                    kind,
                },
            )
            onExistingChange(nextExisting)
            onSelected(selected)
            setPending(null)
            return
        }

        const { selected, nextExisting } = adoptIntoPropertyCatalog(existing, {
            name: trimmed,
            source: pending.kind,
            template: pending.option,
            kind,
        })
        onExistingChange(nextExisting)
        onSelected(selected)
        setPending(null)
    }

    const handleSelectExisting = (option: SetupConfigOption) => {
        const { option: touched, nextExisting } = touchExistingOption(
            existing,
            option.id,
        )
        if (touched) {
            onExistingChange(nextExisting)
            onSelected(createSelectedFromOption(touched, 'existing'))
        } else {
            onSelected(createSelectedFromOption(option, 'existing'))
        }
    }

    const renderSection = ({
        toneKey,
        title,
        icon,
        count,
        children,
    }: {
        toneKey: SetupChooserLane
        title: string
        icon: React.ReactNode
        count?: number
        children: React.ReactNode
    }) => {
        const tone = SECTION_TONES[toneKey]
        return (
            <Box
                className={classes.section}
                style={{ borderLeftColor: tone.accent }}
            >
                <Box className={classes.sectionHeader}>
                    <Box
                        className={classes.sectionIcon}
                        style={{
                            backgroundColor: tone.iconBg,
                            color: tone.iconColor,
                        }}
                    >
                        {icon}
                    </Box>
                    <Typography className={classes.sectionTitle}>
                        {title}
                    </Typography>
                    {count != null ? (
                        <Typography className={classes.sectionCount}>
                            {count}
                        </Typography>
                    ) : null}
                </Box>
                <Box className={classes.sectionBody}>{children}</Box>
            </Box>
        )
    }

    if (loading) {
        return (
            <Box className={classes.loading}>
                <CircularProgress size={28} />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Box className={classes.panel}>
                <Box className={classes.panelHeader}>
                    <Box className={classes.headerIcon}>
                        <TouchAppOutlinedIcon style={{ fontSize: 18 }} />
                    </Box>
                    <Box>
                        <Typography className={classes.headerTitle}>
                            Start by choosing a template or existing config.
                        </Typography>
                        <Typography className={classes.headerDescription}>
                            That becomes the selected configuration you&apos;ll
                            edit here — and what Compose can use later.
                        </Typography>
                    </Box>
                </Box>

                <Box className={classes.grid}>
                    {renderSection({
                        toneKey: 'existing',
                        title: 'Edit existing',
                        icon: <FolderOpenIcon style={{ fontSize: 18 }} />,
                        count: existing.length,
                        children:
                            existing.length === 0 ? (
                                <Typography className={classes.emptySection}>
                                    No property configurations yet.
                                </Typography>
                            ) : (
                                <Box className={classes.cardGrid}>
                                    {existing.map((option) => {
                                        const origin = resolveSetupOrigin(
                                            option,
                                        )
                                        return (
                                            <ConfigOptionCard
                                                key={option.id}
                                                title={option.name}
                                                origin={
                                                    origin ? (
                                                        <SetupOriginLine
                                                            origin={origin}
                                                        />
                                                    ) : null
                                                }
                                                meta={formatModifiedLabel(
                                                    option.modifiedAt,
                                                )}
                                                icon={
                                                    <FolderOpenIcon fontSize="small" />
                                                }
                                                iconBackground={
                                                    SECTION_TONES.existing
                                                        .iconBg
                                                }
                                                iconColor={
                                                    SECTION_TONES.existing
                                                        .iconColor
                                                }
                                                onClick={() =>
                                                    handleSelectExisting(option)
                                                }
                                            />
                                        )
                                    })}
                                </Box>
                            ),
                    })}

                    {renderSection({
                        toneKey: 'org',
                        title: 'Build from org template',
                        icon: <BusinessIcon style={{ fontSize: 18 }} />,
                        count: org.length,
                        children:
                            org.length === 0 ? (
                                <Typography className={classes.emptySection}>
                                    No org templates yet — create one in
                                    EZFolio, or start from EZOS.
                                </Typography>
                            ) : (
                                <Box className={classes.cardGrid}>
                                    {org.map((option) => (
                                        <ConfigOptionCard
                                            key={option.id}
                                            title={option.name}
                                            description={formatTemplateOptionSummary(
                                                option,
                                            )}
                                            meta={formatModifiedLabel(
                                                option.modifiedAt,
                                            )}
                                            icon={
                                                <BusinessIcon fontSize="small" />
                                            }
                                            iconBackground={
                                                SECTION_TONES.org.iconBg
                                            }
                                            iconColor={
                                                SECTION_TONES.org.iconColor
                                            }
                                            onClick={() =>
                                                setPending({
                                                    kind: 'org',
                                                    option,
                                                })
                                            }
                                        />
                                    ))}
                                </Box>
                            ),
                    })}

                    {renderSection({
                        toneKey: 'ezos',
                        title: 'Build from EZOS',
                        icon: <LibraryBooksIcon style={{ fontSize: 18 }} />,
                        count: ezos.length,
                        children:
                            ezos.length === 0 ? (
                                <Typography className={classes.emptySection}>
                                    No EZOS templates available yet.
                                </Typography>
                            ) : (
                                <Box className={classes.cardGrid}>
                                    {ezos.map((option) => (
                                        <ConfigOptionCard
                                            key={option.id}
                                            title={option.name}
                                            description={formatTemplateOptionSummary(
                                                option,
                                            )}
                                            meta={formatModifiedLabel(
                                                option.modifiedAt,
                                            )}
                                            icon={
                                                <LibraryBooksIcon fontSize="small" />
                                            }
                                            iconBackground={
                                                SECTION_TONES.ezos.iconBg
                                            }
                                            iconColor={
                                                SECTION_TONES.ezos.iconColor
                                            }
                                            onClick={() =>
                                                setPending({
                                                    kind: 'ezos',
                                                    option,
                                                })
                                            }
                                        />
                                    ))}
                                </Box>
                            ),
                    })}

                    {renderSection({
                        toneKey: 'scratch',
                        title: 'Create your own',
                        icon: <AddIcon style={{ fontSize: 18 }} />,
                        children: (
                            <Box className={classes.cardGrid}>
                                <ConfigOptionCard
                                    title="Build from scratch"
                                    description={
                                        discourageScratch
                                            ? 'Prefer EZOS or org templates when state or catalog rules apply.'
                                            : 'Blank configuration — set everything yourself.'
                                    }
                                    icon={<AddIcon fontSize="small" />}
                                    iconBackground={
                                        SECTION_TONES.scratch.iconBg
                                    }
                                    iconColor={SECTION_TONES.scratch.iconColor}
                                    muted
                                    onClick={() =>
                                        setPending({
                                            kind: 'scratch',
                                            defaultName: scratchDefaultName(
                                                kind,
                                            ),
                                        })
                                    }
                                />
                            </Box>
                        ),
                    })}
                </Box>
            </Box>

            <NameConfigDialog
                open={pending != null}
                title="Name your configuration"
                confirmLabel={
                    pending?.kind === 'org' ? 'Apply & open' : 'Create & open'
                }
                initialName={
                    pending?.kind === 'scratch'
                        ? pending.defaultName
                        : pending?.option.name ?? ''
                }
                submitting={false}
                onClose={closeDialog}
                onConfirm={handleConfirmName}
            />
        </Box>
    )
}
